import clsx from 'clsx'
import React, { useContext, useLayoutEffect } from 'react'

import { HeadlessListbox } from 'slab/components/HeadlessListbox'
import { Popper } from 'slab/components/Popper'

import { Presence } from '../Presence'
import { AutocompleteContext } from './context'
import { DismissibleLayer } from '../DismissibleLayer'

interface AutocompleteListProps {
  /**
   * Should the list be visually attached to the input? If true, the list will
   * share a border with the input. If false, it will be appear as a floating
   * element above or below the input.
   */
  attached?: boolean;
  children?: React.ReactNode;
  positionStrategy?: 'fixed' | 'absolute';
  withPortal?: boolean;
  zIndex?: number;
}

export function AutocompleteList ({ attached = true, children, withPortal = true, zIndex, positionStrategy = 'absolute' }: AutocompleteListProps) {
  const { inputRef, listId, open, setOpen } = useContext(AutocompleteContext)

  const hasChildren = React.Children.count(children) > 0

  useLayoutEffect(() => {
    if (!hasChildren) {
      setOpen(false)
    }
  })

  /**
   * Prevents closing when:
   * - clicking the input itself
   * - clicking the input's label, which will move focus right back to the input
   *   and cause an unsightly flash of the dropdown
   * - clicking other interactive elements within Slab components like
   *   `<TextInput />`, e.g. the "x" button to clear the input
   */
  function handleInteractOutside (event: FocusEvent | MouseEvent) {
    const label = document.querySelector(
      'label[for="' + inputRef.current?.id + '"]'
    )
    const formField = document.querySelector(
      '[data-form-field-for="' + inputRef.current?.id + '"]'
    )

    const interactedInLabel = label?.contains(event.target as Node)
    const interactedInFormField = formField?.contains(event.target as Node)
    const interactedInInput = inputRef.current?.contains(event.target as Node)

    if (!(interactedInLabel || interactedInFormField || interactedInInput)) {
      setOpen(false)
    }
  }

  const content = (
    <Presence asChild present={open && hasChildren}>
      <DismissibleLayer
        asChild
        onEscapeKey={() => setOpen(false)}
        onFocusOutside={handleInteractOutside}
        onPointerOutside={handleInteractOutside}
      >
        <Popper.Content
          asChild
          collisionPadding={16}
          positionStrategy={positionStrategy}
          shift={false}
          sideOffset={attached ? 0 : 4}
          tabIndex={-1}
          zIndex={zIndex}
        >
          <HeadlessListbox.List
            aria-label='Suggestions'
            className={clsx(
              'py-1 bg-white shadow w-[--popper-anchor-width] max-h-[--popper-available-height] overflow-y-auto',
              'group/autocomplete-list',
              // Note: Using the `duration-300` utility class for some reason
              // causes changes to the element's height to be animated, which we
              // don't want, so we're using the arbitrary `[animation-duration:300ms]`
              // utility class instead.
              'animate-in fade-in [animation-duration:300ms]',
              {
                'border-x border-gray-200 data-[side=top]:border-t data-[side=bottom]:border-b data-[side=bottom]:rounded-b-lg data-[side=top]:rounded-t-lg':
                  attached,
                'border border-gray-200 rounded-lg': !attached,
              }
            )}
            id={listId}
          >
            <div className='animate-in [animation-duration:300ms] group-data-[side=top]/autocomplete-list:slide-in-from-bottom-1 group-data-[side=bottom]/autocomplete-list:slide-in-from-top-1'>
              {children}
            </div>
          </HeadlessListbox.List>
        </Popper.Content>
      </DismissibleLayer>
    </Presence>
  )

  if (withPortal) {
    return <Popper.Portal>{content}</Popper.Portal>
  }

  return content
}

AutocompleteList.displayName = 'Autocomplete.List'
